import type { ActionPhaseName, SpinResponse } from "@slot/client-engine";
import { fx, spinFixture } from "@slot/game-shell";
import { GRID_CELL_COUNT, maskOf } from "../gridSize";
import { POTS, type PotLevels } from "../rules/symbols";
import { GROW_CELLS, encodeBoard } from "./holdRules";
import { BIG_WIN_TIERS, RESPINS } from "./offlineMath";
import type { BonusSession, RespinStep, RoundOutcome } from "./roundTypes";

/** The bonus's feature id on the wire. */
export const HOLD_AND_WIN_FEATURE_ID = "hold_and_win";

/** The event builder `spinFixture(...).event` hands over. */
type FixtureEvent = Parameters<Parameters<ReturnType<typeof spinFixture>["event"]>[2]>[0];

/** A canonical action from the shell's DSL, authored with its phase. */
function act(e: FixtureEvent, a: { type: string; phase: ActionPhaseName; payload: object }): void {
  e.action(a.type, a.payload as Record<string, unknown>, { actionPhaseName: a.phase });
}

/** One of this title's actions (or the hold mechanic's), in its phase: never skippable, every one changes state. */
function own(e: FixtureEvent, type: string, payload: Record<string, unknown>, phase: ActionPhaseName = "other"): void {
  e.action(type, payload, { actionPhaseName: phase });
}

const levels = (pots: PotLevels): Record<string, string> => Object.fromEntries(POTS.map((pot) => [pot, String(pots[pot])]));

const ALL_CELLS = Array.from({ length: GRID_CELL_COUNT }, (_, i) => i);

function bigWinTier(winMinor: bigint, betMinor: bigint): string | null {
  let tier: string | null = null;
  for (const { levelIdentifier, minWinMultiplier } of BIG_WIN_TIERS) if (winMinor >= betMinor * BigInt(minWinMultiplier)) tier = levelIdentifier;
  return tier;
}

/**
 * A round outcome as the backend would send it (the wire contract, `.claude/docs/game-design.md`): the spin's frame with
 * its hold, boundary, pots and trigger; then, for a bonus, `feature_enter` with the boosters, a `respin` and a
 * `respin_result` per respin (the meter as the respin starts, then as it lands), the end of the bonus in the last
 * `respin_result`, and `feature_exit` with the total.
 */
export function buildRound(input: { roundIdentifier: string; outcome: RoundOutcome; currencyCode: string; balanceMinor: string }): SpinResponse {
  const { outcome } = input;
  // Every event carries the bet and the lines it covers (decision 56): its total is the bet.
  const bet = outcome.betMinor;
  const lines = outcome.lines;
  const spin = outcome.spin;
  const builder = spinFixture(input.roundIdentifier).balance(input.currencyCode, input.balanceMinor);
  let seq = 0;

  builder.event("spin", seq++, (e) => {
    e.gridEncoded(encodeBoard(spin.board)).betAmount(bet).betLines(lines).accumulatedWinAfter(outcome.bonus ? "0" : outcome.winMinor);
    if (spin.anticipatedCells.length > 0) e.anticipateCells([...spin.anticipatedCells]);
    own(e, "hold_cells", { positionMask: maskOf(GROW_CELLS) }, "frame_prepare");
    act(e, fx.spinBoundaryStart("base:1", "base"));
    own(e, "update_pots", {
      // The pots hold at a line's bet: another line count keeps them, another bet empties them.
      betMinor: outcome.lineBetMinor,
      levelsBefore: levels(spin.potsBefore),
      marks: spin.marks.map((m) => ({ positionIndex: String(m.index), pot: m.pot })),
      levelsAfter: levels(spin.potsAfter),
    });
    if (spin.trigger) {
      own(e, "present_bonus_trigger", { reason: spin.trigger.reason, positionMask: maskOf(spin.trigger.cells) });
      act(e, fx.bonusFeatureWon(HOLD_AND_WIN_FEATURE_ID, String(RESPINS)));
    }
    act(e, fx.spinBoundaryEnd("base:1", "0"));
  });

  const bonus = outcome.bonus;
  if (bonus) {
    builder.event("feature_enter", seq++, (e) => {
      e.betAmount(bet).betLines(lines).featureStateSet({ featureIdentifier: HOLD_AND_WIN_FEATURE_ID, featureCountRemaining: String(RESPINS), featureAccumulatedWinMinor: bonus.startValueMinor, featurePhase: "enter" });
      // What the bonus's values are multiples of: a line's bet, or a bought bonus's whole bet (the plaques show it).
      own(e, "activate_boosters", { ...bonus.boosters, valueBetMinor: outcome.valueBetMinor });
    });
    let value = bonus.startValueMinor;
    bonus.respins.forEach((step, k) => {
      const boundary = `respin:${k + 1}`;
      builder.event("respin", seq++, (e) => {
        e.gridEncoded(encodeBoard(step.landedBoard))
          .betAmount(bet).betLines(lines)
          .featureStateSet({ featureIdentifier: HOLD_AND_WIN_FEATURE_ID, featureCountRemaining: String(step.countAtStart), featureAccumulatedWinMinor: value, featurePhase: "update" });
        if (step.refilled) own(e, "refill_respins", { respins: String(RESPINS) }, "frame_prepare");
        own(e, "hold_cells", { positionMask: maskOf(step.held) }, "frame_prepare");
        act(e, fx.spinBoundaryStart(boundary, HOLD_AND_WIN_FEATURE_ID));
        for (const collect of step.collects) {
          own(e, "collect_coins", {
            collectorIndex: String(collect.collectorIndex),
            sources: collect.sources.map((s) => ({ positionIndex: String(s.index), amountMinor: s.amountMinor })),
            valueMinor: collect.valueMinor,
          });
        }
        if (step.drops.length > 0) own(e, "drop_multipliers", { cells: step.drops.map((d) => ({ positionIndex: String(d.index), multiplier: String(d.multiplier) })) });
      });
      value = step.boardValueMinor;
      const last = k === bonus.respins.length - 1;
      builder.event("respin_result", seq++, (e) => resultEvent(e, { step, bet, lines, boundary, bonus: last ? bonus : null }));
    });
    if (bonus.respins.length === 0) {
      // A trigger board already full: no respin, the end of the bonus at once.
      builder.event("respin_result", seq++, (e) => {
        e.gridEncoded(encodeBoard(bonus.startBoard)).betAmount(bet).betLines(lines).featureStateSet({ featureIdentifier: HOLD_AND_WIN_FEATURE_ID, featureCountRemaining: String(RESPINS), featureAccumulatedWinMinor: bonus.startValueMinor, featurePhase: "update" });
        own(e, "hold_cells", { positionMask: maskOf(ALL_CELLS) }, "frame_prepare");
        act(e, fx.spinBoundaryStart("respin:0", HOLD_AND_WIN_FEATURE_ID));
        endOfBonus(e, bonus, bet);
        act(e, fx.spinBoundaryEnd("respin:0", bonus.end.totalMinor));
      });
    }
    builder.event("feature_exit", seq++, (e) => {
      e.betAmount(bet).betLines(lines)
        .featureStateSet({ featureIdentifier: HOLD_AND_WIN_FEATURE_ID, featureCountRemaining: "0", featureAccumulatedWinMinor: bonus.end.totalMinor, featurePhase: "exit" })
        .accumulatedWinAfter(bonus.end.totalMinor);
    });
  }
  return builder.build();
}

/** A respin's landing state: the meter and the total once it landed; on the last respin, the end of the bonus. */
function resultEvent(e: FixtureEvent, input: { step: RespinStep; bet: string; lines: number; boundary: string; bonus: BonusSession | null }): void {
  const { step, bet, lines, boundary, bonus } = input;
  e.gridEncoded(encodeBoard(step.board))
    .betAmount(bet).betLines(lines)
    .featureStateSet({ featureIdentifier: HOLD_AND_WIN_FEATURE_ID, featureCountRemaining: String(step.countAfter), featureAccumulatedWinMinor: step.boardValueMinor, featurePhase: "update" });
  own(e, "hold_cells", { positionMask: maskOf(ALL_CELLS) }, "frame_prepare");
  if (bonus) endOfBonus(e, bonus, bet);
  act(e, fx.spinBoundaryEnd(boundary, bonus ? bonus.end.totalMinor : "0"));
}

/** The end of the bonus: the mysteries revealed, the full board's jackpot, every cell's pay into the total, the big win. */
function endOfBonus(e: FixtureEvent, bonus: BonusSession, bet: string): void {
  const end = bonus.end;
  if (end.mysteries.length > 0) own(e, "reveal_mysteries", { cells: end.mysteries.map((m) => ({ positionIndex: String(m.index), symbolId: m.symbolId })) });
  if (end.fullBoard) own(e, "award_full_board", { jackpot: end.fullBoard.jackpot, amountMinor: end.fullBoard.amountMinor });
  own(e, "collect_board", { cells: end.cells.map((c) => ({ positionIndex: String(c.index), amountMinor: c.amountMinor })), totalMinor: end.totalMinor });
  act(e, fx.updateTotalWinMeter(end.totalMinor));
  const tier = bigWinTier(BigInt(end.totalMinor), BigInt(bet));
  if (tier) {
    act(e, fx.presentBigWin({ levelIdentifier: tier, spinWinAmount: end.totalMinor, betAmount: bet, winRatioLabel: `${BigInt(end.totalMinor) / BigInt(bet)}x` }));
  }
}
